import { CapacitorHttp } from '@capacitor/core';
import { CapacitorWifi } from '@capgo/capacitor-wifi';

import {
    deviceIdFromSetupCode,
    normalizeNetworks,
    pinnableWifiChannel,
    ProvisioningTransport,
    WifiJoinOptions,
    WifiNetwork
} from './deviceProvisioning';
import {
    provisioningErrorTranslationKeys,
    wifiJoinErrorFromHttpFailure,
    wifiJoinErrorFromHttpResponse
} from './wifiProvisioningErrors';

const SETUP_DEVICE_URL = 'http://192.168.169.1';
const SETUP_REACHABLE_TIMEOUT_MS = 20000;
const SETUP_REACHABLE_POLL_INTERVAL_MS = 1000;

export class SoftApProvisioningTransport implements ProvisioningTransport {
    readonly mode = 'softap' as const;
    readonly deviceId: string;
    readonly displayName: string;
    readonly setupCode: string;

    private connectedSsid?: string;
    private joinResponseLost = false;

    constructor(setupCode: string) {
        this.setupCode = setupCode;
        this.deviceId = deviceIdFromSetupCode(setupCode);
        this.displayName = `OnlyCat-${setupCode.substring(6, 12)}`;
    }

    get checkConnectivityOnClaim(): boolean {
        return this.joinResponseLost;
    }

    async prepare(signal?: AbortSignal): Promise<void> {
        const ssid = this.displayName;

        try {
            await CapacitorWifi.connect({
                ssid,
                autoRouteTraffic: true
            });
        }
        catch (e: any) {
            if (!(e?.code === 13 || e?.message?.includes('already associated'))) {
                throw e;
            }
        }

        this.connectedSsid = ssid;
        await this.waitUntilReachable(signal);
    }

    // The OS reports the setup AP as joined before DHCP and routing are actually
    // usable, so poll the device's health endpoint until it answers.
    private async waitUntilReachable(signal?: AbortSignal): Promise<void> {
        const deadline = Date.now() + SETUP_REACHABLE_TIMEOUT_MS;

        while (true) {
            if (signal?.aborted) {
                throw new DOMException('Provisioning cancelled', 'AbortError');
            }

            try {
                await CapacitorHttp.get({
                    url: `${SETUP_DEVICE_URL}/`,
                    connectTimeout: SETUP_REACHABLE_POLL_INTERVAL_MS,
                    readTimeout: SETUP_REACHABLE_POLL_INTERVAL_MS
                });
                return;
            }
            catch {
                if (Date.now() >= deadline) {
                    throw new Error('device_setup_unreachable');
                }
            }

            await delay(SETUP_REACHABLE_POLL_INTERVAL_MS);
        }
    }

    async scanWifiNetworks(): Promise<WifiNetwork[]> {
        const res = await CapacitorHttp.get({ url: `${SETUP_DEVICE_URL}/setup/wifi/scan`, responseType: 'json' });
        return normalizeNetworks(res.data ?? []);
    }

    async joinWifiNetwork(ssid: string, password: string, options?: WifiJoinOptions): Promise<void> {
        const channel = pinnableWifiChannel(options?.channel);

        try {
            const res = await CapacitorHttp.post({
                url: `${SETUP_DEVICE_URL}/setup/wifi/join`,
                data: JSON.stringify(channel ? { ssid, password, channel } : { ssid, password }),
                headers: { 'Content-Type': 'application/json' },
                responseType: 'json'
            });

            if (res.status !== 200) {
                throw wifiJoinErrorFromHttpResponse(res.data);
            }
        }
        catch (e: any) {
            // A device-reported failure keeps the setup AP up and the phone on it,
            // so the user can go back and retry without reopening the modal.
            if (provisioningErrorTranslationKeys.has(e?.message)) {
                throw e;
            }

            const joinError = wifiJoinErrorFromHttpFailure(e);
            if (joinError) {
                throw joinError;
            }

            this.joinResponseLost = true;
            console.warn('Device Wi-Fi join response was lost; proceeding to claim with connectivity check', e);
        }

        await this.cleanup();
    }

    async cleanup(): Promise<void> {
        const connectedSsid = this.connectedSsid;
        this.connectedSsid = undefined;

        if (!connectedSsid) {
            return;
        }

        try {
            await CapacitorWifi.disconnect({ ssid: connectedSsid });
            console.log('Disconnected from', connectedSsid);
        }
        catch (e: any) {
            console.error('Failed to disconnect from', connectedSsid, e);
        }
    }
}

function delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
}
